import { FetchError } from './fetchError';
import { FetchResponse } from './fetchResponse';

type UrlParams = URLSearchParams | {} | string;

export interface CallFetchOptions<B = unknown> {
    headers?: Headers;
    method?: string;
    body?: B;
    params?: UrlParams;
}

export function resolveUrlSearchParams(params?: UrlParams): URLSearchParams | undefined {
    if (!params) {
        return undefined;
    }
    if (params instanceof URLSearchParams) {
        return params;
    }
    return new URLSearchParams(params);
}

/**
 * Checks and resolves a params object, or URLSearchParams, or string
 * If the url already has params, it will append the new params
 *
 * @returns the url string with the params
 */
export function resolveUrl(url: string, params?: UrlParams): string {
    if (params) {
        const searchParams = resolveUrlSearchParams(params)?.toString();
        if (searchParams) {
            if (url.includes('?')) {
                return `${url}&${searchParams.toString()}`;
            }
            return `${url}?${searchParams.toString()}`;
        }
    }
    return url;
}

/**
 *  Checks if there are custom headers, and if so, they set the Content-Type,
 *  otherwise it sets the default application/json. To keep in line with axios does.
 *
 *  If no headers are passed, it will return the default headers.
 */
export function resolveHeaders(headers?: Headers): Headers {
    if (headers) {
        if (!headers.has('Content-Type')) {
            const newHeaders = new Headers(headers);
            newHeaders.set('Content-Type', 'application/json');
            return newHeaders;
        }
        return headers;
    }

    return new Headers({ 'Content-Type': 'application/json' });
}

/**
 * Checks if the method is one of [PUT, POST, PATCH], which allows a body,
 * and handles the body if content-type is set to application/json, which is the most common in REST.
 *
 * Otherwise just returns the body, and leave to the caller to handle Buffers, FormData, etc.
 */
export function resolveBody(method: string, contentType: string, body?: unknown): BodyInit | undefined {
    if (!['POST', 'PUT', 'PATCH'].includes(method) || body === undefined) {
        return undefined;
    }

    if (contentType.includes('application/json')) {
        if (typeof body === 'string') {
            return body;
        }
        if (typeof body === 'object') {
            return JSON.stringify(body);
        }
    }

    return body as BodyInit;
}

export async function apiFetch<T = unknown, E = unknown>(
    baseUrl: string,
    { headers, method = 'GET', body, params }: CallFetchOptions = {}
): Promise<FetchResponse<T>> {
    const url = resolveUrl(baseUrl, params);
    const requestInit: RequestInit & { headers: Headers } = {
        method,
        headers: resolveHeaders(headers),
    };

    const resolvedBody = resolveBody(method, requestInit.headers.get('Content-Type') || '', body);

    // can still be an empty string
    if (resolvedBody !== undefined) {
        requestInit.body = resolvedBody;
    }

    const response = await fetch(url, requestInit);

    let data;
    if (response.headers.get('Content-Type')?.includes('application/json')) {
        data = await response.json();
    }

    if (!response.ok) {
        throw new FetchError<E>(response, data);
    }

    return new FetchResponse<T>(response, data);
}

export function apiPost<T = unknown, E = unknown>(
    baseUrl: string,
    body?: unknown,
    options?: Omit<CallFetchOptions, 'method' | 'body'>
): Promise<FetchResponse<T>> {
    return apiFetch<T, E>(baseUrl, { ...options, method: 'POST', body });
}

export function apiGet<T = unknown, E = unknown>(
    baseUrl: string,
    options?: Omit<CallFetchOptions, 'method' | 'body'>
): Promise<FetchResponse<T>> {
    return apiFetch<T, E>(baseUrl, { ...options, method: 'GET' });
}
